import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ReceivedFeedbacks } from './sections/ReceivedFeedbacks';
import GivenFeedbacks from './sections/GivenFeedbacks';
import FeedbackRequestsList from '@/components/feedback-requests/FeedbackRequestsList';
import {
  PieChart,
  FileText,
  MessageSquare,
  Send,
  BarChart3,
  Inbox,
  Mail,
  Plus,
  ClipboardList
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import ProjectsTab from '@/components/projects/ProjectsTab';
import { useRoleMapping } from '@/hooks/useRoleMapping';

const ModernDashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');
  const [feedbackSubType, setFeedbackSubType] = useState<'received' | 'given'>('received');
  const [requestsSubType, setRequestsSubType] = useState<'received' | 'sent'>('received');
  
  const { isAdmin, isCorporate, isIndividual } = useRoleMapping();

  const handleCreateFeedback = () => {
    navigate('/create-feedback');
  };

  const handleRequestFeedback = () => {
    navigate('/request-feedback');
  };

  const getFeedbackContext = () => {
    if (isIndividual) return 'individual';
    if (isCorporate) return 'corporate';
    return 'individual'; // Valeur par défaut
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-blue-700 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Feedbacks reçus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">5</p>
                <p className="text-sm text-blue-600">Dernière semaine</p>
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                onClick={() => {
                  setActiveTab('feedbacks');
                  setFeedbackSubType('received');
                }}
              >
                Voir tous
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-green-700 flex items-center">
              <Send className="h-5 w-5 mr-2" />
              Feedbacks donnés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">3</p>
                <p className="text-sm text-green-600">Dernière semaine</p>
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-green-600 hover:text-green-800 hover:bg-green-50"
                onClick={() => {
                  setActiveTab('feedbacks');
                  setFeedbackSubType('given');
                }}
              >
                Voir tous
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-purple-700 flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Demandes en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold">2</p>
                <p className="text-sm text-purple-600">À traiter</p>
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                onClick={() => {
                  setActiveTab('requests');
                  setRequestsSubType('received');
                }}
              >
                Voir toutes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Button 
          onClick={handleRequestFeedback}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Demander un feedback
        </Button>
        
        {(isCorporate || isAdmin) && (
          <Button 
            onClick={handleCreateFeedback}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un feedback
          </Button>
        )}
        
        <Button 
          variant="outline" 
          onClick={() => {
            setActiveTab('projects');
          }}
        >
          <ClipboardList className="h-4 w-4 mr-2" />
          Voir mes projets
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 mb-8">
          <TabsTrigger value="overview" className="flex items-center">
            <BarChart3 className="h-4 w-4 mr-2 hidden md:inline" />
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="feedbacks" className="flex items-center">
            <FileText className="h-4 w-4 mr-2 hidden md:inline" />
            Feedbacks
          </TabsTrigger>
          <TabsTrigger value="requests" className="flex items-center">
            <MessageSquare className="h-4 w-4 mr-2 hidden md:inline" />
            Demandes
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center">
            <ClipboardList className="h-4 w-4 mr-2 hidden md:inline" />
            Projets
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Activité récente</CardTitle>
              </CardHeader>
              <CardContent className="px-2">
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-full text-blue-700">
                        <Inbox className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">Nouveau feedback reçu</p>
                          <Badge variant="outline" className="text-xs">Il y a 2 jours</Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">De: Tech Solutions - Poste: Développeur Frontend</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-full text-green-700">
                        <Send className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">Feedback envoyé</p>
                          <Badge variant="outline" className="text-xs">Il y a 4 jours</Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">À: John Doe - Poste: Product Manager</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-full text-purple-700">
                        <MessageSquare className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">Demande de feedback reçue</p>
                          <Badge variant="outline" className="text-xs">Il y a 6 jours</Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">De: Global Tech - Poste: Chef de Projet</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Statistiques</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center">
                <div className="w-32 h-32 relative flex items-center justify-center">
                  <PieChart className="w-full h-full text-gray-200" />
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <p className="text-3xl font-bold">85%</p>
                    <p className="text-xs text-gray-500">Complet</p>
                  </div>
                </div>
                
                <div className="mt-6 w-full space-y-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Feedbacks reçus</span>
                      <span className="font-medium">5/6</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="bg-blue-500 h-full w-5/6"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Feedbacks donnés</span>
                      <span className="font-medium">3/4</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="bg-green-500 h-full w-3/4"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Projets</span>
                      <span className="font-medium">2/2</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="bg-purple-500 h-full w-full"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="feedbacks">
          <Card>
            <CardHeader className="border-b pb-3">
              <div className="flex justify-between items-center">
                <CardTitle>Mes Feedbacks</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={feedbackSubType === 'received' ? 'default' : 'outline'}
                    onClick={() => setFeedbackSubType('received')}
                    className={`${feedbackSubType === 'received' ? 'bg-blue-600' : ''}`}
                  >
                    <Inbox className="h-4 w-4 mr-2" />
                    Reçus
                  </Button>
                  <Button
                    size="sm"
                    variant={feedbackSubType === 'given' ? 'default' : 'outline'}
                    onClick={() => setFeedbackSubType('given')}
                    className={`${feedbackSubType === 'given' ? 'bg-green-600' : ''}`}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Donnés
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {feedbackSubType === 'received' ? (
                <ReceivedFeedbacks isCandidat={isIndividual} isEntreprise={isCorporate} />
              ) : (
                <GivenFeedbacks isCandidat={isIndividual} isEntreprise={isCorporate} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="requests">
          <Card>
            <CardHeader className="border-b pb-3">
              <div className="flex justify-between items-center">
                <CardTitle>Mes Demandes de Feedback</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={requestsSubType === 'received' ? 'default' : 'outline'}
                    onClick={() => setRequestsSubType('received')}
                    className={`${requestsSubType === 'received' ? 'bg-blue-600' : ''}`}
                  >
                    <Inbox className="h-4 w-4 mr-2" />
                    Reçues
                  </Button>
                  <Button
                    size="sm"
                    variant={requestsSubType === 'sent' ? 'default' : 'outline'}
                    onClick={() => setRequestsSubType('sent')}
                    className={`${requestsSubType === 'sent' ? 'bg-green-600' : ''}`}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Envoyées
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <FeedbackRequestsList 
                type={requestsSubType} 
                context={getFeedbackContext()}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="projects">
          <ProjectsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ModernDashboard;
